import React from 'react';
import _ from 'lodash';
import {
  Card,
  CardContent,
  Radio,
  FormControlLabel,
  FormControl,
  RadioGroup,
  FormLabel,
  Stack,
  Typography,
  Chip,
  Box,
  CardHeader,
  IconButton,
  Divider
} from '@mui/material/';
import { useSelector } from 'react-redux';
import { OLPeriods } from './periods/OLPeriods';
import { ZSPeriods } from './periods/ZSPeriods';
import { SmallFormDateField } from '../../../../../components/small-fields/SmallFormDateField';
import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { formatDate } from '../../../../../services/functions';
import { useApplication } from '../../ApplicationsContext';
import { useDidMountEffect } from '../../../../../services/hooks';
import CancelIcon from '@mui/icons-material/Cancel';

export const Periods = ({ formik }) => {
  const { rowData, choices } = useApplication();
  const { seasonYear } = useSelector(state => state.app);

  useDidMountEffect(() => {
    const worksInAdditionalPeriodsValueDisplayed = _.first(
      _.filter(
        choices.works_in_additional_periods,
        obj => obj.value == formik.values.works_in_additional_periods
      )
    );
    const AnnexParagraph1DefaultText = `Strony zgodnie uzgadniają, że Zleceniobiorca ${_.get(
      worksInAdditionalPeriodsValueDisplayed,
      'label',
      ''
    ).toLowerCase()} usługi w terminie ${
      formik.values.changed_periods ? formik.values.changed_periods : ''
    } (zgodnie z poniższą tabelą):`;
    formik.setFieldValue('annex_paragraph1_text', AnnexParagraph1DefaultText);
  }, [
    formik.values.changed_periods,
    formik.values.works_in_additional_periods
  ]);

  const updatePeriod = e => {
    const findRemovedPeriod = formik.values.periods.filter(
      c => !e.target.value.includes(c)
    );
    if (_.isEmpty(findRemovedPeriod)) {
      formik.setFieldValue('periods', e.target.value.sort());
    } else {
      formik.setFieldValue('periods', e.target.value.sort());
    }
  };

  const updateAgreementDates = periods => {
    const periodDates = !_.isEmpty(periods)
      ? _.flatten(periods.map(period => period.fixed_date))
      : [];
    periodDates.sort();
    const minDate = _.first(periodDates) ? _.first(periodDates) : null;
    const maxDate = _.last(periodDates) ? _.last(periodDates) : null;

    if (minDate != formik.values.agreement_date_from) {
      formik.setFieldValue('agreement_date_from', minDate);
    }
    if (maxDate != formik.values.agreement_date_to && maxDate) {
      formik.setFieldValue('agreement_date_to', maxDate);
    }
  };

  const changePerdiodType = e => {
    formik.setFieldValue('periods_type', e.target.value);
    if (formik.values.periods_type != 'obozy') {
      updateAgreementDates(formik.values.ol_timed_periods);
    } else if (formik.values.periods_type != 'zielone_szkoly') {
      updateAgreementDates(formik.values.zs_timed_periods);
    }
  };

  return (
    <Stack direction="column" alignItems="center" justifyContent="start">
      <Card sx={{ width: '100%' }}>
        <CardHeader title="Dostępny w terminach"></CardHeader>
        <CardContent>
          <Stack flex="1 1 30%">
            <SmallFormTextField
              name="periods"
              select
              multiple
              placeholder=""
              value={formik.values.periods}
              onChange={e => updatePeriod(e)}
              options={rowData.all_periods_choices || []}
              sx={{ width: '100%' }}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(value => {
                    const choice = rowData.all_periods_choices
                      ? rowData.all_periods_choices.find(
                        element => value === element.value
                      )
                      : undefined;

                    return <Chip key={value} label={choice?.label} />;
                  })}
                </Box>
              )}
            />
          </Stack>
        </CardContent>
      </Card>
      <Card sx={{ width: '100%' }}>
        <CardHeader title="Zatrudniony w terminach"></CardHeader>
        <CardContent>
          <Stack sx={{ mb: 3 }}>
            <FormControl>
              <RadioGroup
                row
                value={formik.values.periods_type}
                onChange={e => changePerdiodType(e)}
              >
                <FormControlLabel
                  value="obozy"
                  control={<Radio />}
                  label="Obozy"
                />
                <FormControlLabel
                  value="zielone_szkoly"
                  control={<Radio />}
                  label="Zielone szkoły"
                />
              </RadioGroup>
            </FormControl>
          </Stack>
          {formik.values.periods_type == 'obozy' ? (
            <OLPeriods
              formik={formik}
              data={rowData}
              updateAgreementDates={updateAgreementDates}
            />
          ) : (
            <ZSPeriods
              formik={formik}
              data={rowData}
              updateAgreementDates={updateAgreementDates}
            />
          )}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ my: 1, width: '100%' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
            >
              <Typography variant="subtitle2">Termin umowy od</Typography>
              <SmallFormDateField
                standardTextField
                name="agreement_date_from"
                id="agreement_date_from"
                type="text"
                placeholder="termin umowy od"
                value={formik.values.agreement_date_from}
                onChange={val => formik.setFieldValue('agreement_date_from', formatDate(val))}
                error={Boolean(formik.errors.agreement_date_from)}
                helperText={
                  formik.touched.agreement_date_from &&
                  formik.errors.agreement_date_from
                }
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
            >
              <Typography variant="subtitle2">Termin umowy do</Typography>
              <SmallFormDateField
                standardTextField
                name="agreement_date_to"
                id="agreement_date_to"
                type="text"
                placeholder="termin umowy do"
                value={formik.values.agreement_date_to}
                onChange={val => formik.setFieldValue('agreement_date_to', formatDate(val))}
                error={Boolean(formik.errors.agreement_date_to)}
                helperText={
                  formik.touched.agreement_date_to &&
                  formik.errors.agreement_date_to
                }
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Card sx={{ width: '100%' }}>
        <CardHeader title="Dodatkowe informacje"></CardHeader>
        <CardContent>
          <Stack
            direction="row"
            alignItems="flex-center"
            justifyContent="center"
            spacing={1}
          >
            <Typography
              variant="subtitle2"
              sx={{
                width: '50%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'pre'
              }}
            >
              Data umowy / aneksu (opcjonalnie)
            </Typography>
            <Stack direction="row" sx={{ width: '50%' }}>
              <SmallFormDateField
                sx={{ flexGrow: 1 }}
                standardTextField
                name="date"
                id="date"
                type="text"
                placeholder="Data umowy / aneksu"
                value={formik.values.date}
                onChange={val => formik.setFieldValue('date', formatDate(val))}
                error={Boolean(formik.errors.date)}
                helperText={formik.touched.date && formik.errors.date}
              />
              <IconButton>
                <CancelIcon
                  onClick={() => formik.setFieldValue('date', null)}
                />
              </IconButton>
            </Stack>
          </Stack>
          <Box height={20}/>
          <Divider p={3} textAlign="left"><b>Aneksy</b></Divider>
          {seasonYear >= 2024
            ? <Stack
              direction="row"
              sx={{ mt: 1 }}
              alignItems="flex-start"
              justifyContent="left"
              spacing={1}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  width: '50%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'pre'
                }}
              >
                Wynagrodzenie ulega / nie ulega zmianie
              </Typography>
              <SmallFormTextField
                name="salaryChangeInfo"
                select
                placeholder="Wynagrodzenie ulega / nie ulega zmianie"
                value={formik.values.salary_change_info}
                onChange={e => formik.setFieldValue('salary_change_info', e.target.value)}
                options={choices.salary_change_info}
                sx={{ width: '50%' }}
              />
            </Stack>
            : null}

          <Stack
            direction="row"
            sx={{ mt: 1 }}
            alignItems="flex-start"
            justifyContent="left"
            spacing={1}
          >
            <Typography
              variant="subtitle2"
              sx={{
                width: '50%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'pre'
              }}
            >
              Świadczy dodatkowo / nie świadczy usługi w terminach
            </Typography>
            <SmallFormTextField
              name="worksInAdditionalPeriods"
              select
              placeholder="Informacja do aneksów"
              value={formik.values.works_in_additional_periods}
              onChange={e => formik.setFieldValue('works_in_additional_periods', e.target.value)}
              options={choices.works_in_additional_periods}
              sx={{ width: '50%' }}
            />
          </Stack>
          {formik.values.works_in_additional_periods ? (
            <Stack
              direction="row"
              sx={{ mt: 1 }}
              alignItems="flex-start"
              justifyContent="left"
              spacing={1}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  width: '50%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'pre'
                }}
              >
                Edytowane terminy
              </Typography>
              <SmallFormTextField
                name="changed_periods"
                placeholder=""
                value={formik.values.changed_periods}
                onChange={formik.handleChange}
                sx={{ width: '50%' }}
                multiline={true}
                rows={3}
              />
            </Stack>
          ) : null}
          {formik.values.works_in_additional_periods ? (
            <Stack
              direction="row"
              sx={{ mt: 1 }}
              alignItems="flex-start"
              justifyContent="left"
              spacing={1}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  width: '50%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'pre'
                }}
              >
                Treść paragrafu §1
              </Typography>
              <SmallFormTextField
                name="annex_paragraph1_text"
                value={formik.values.annex_paragraph1_text}
                onChange={formik.handleChange}
                sx={{ width: '50%' }}
                multiline={true}
                rows={3}
              />
            </Stack>
          ) : null}
        </CardContent>
      </Card>
    </Stack>
  );
};
